import { useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { useUser } from "@components/user-context";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import {
  MCAttributionWindowDays,
  NBAccountingMode,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import {
  AttributionWindowSelect,
  useAttributionWindowOptions,
} from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import { AttributionModelSelect } from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";

interface Props {
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  disabled?: boolean;
  options?: { value: string; label: string }[];
  onAttributionModelChange: (attributionModel: string) => void;
  onAttributionWindowChange: (
    attributionWindow: IMCAttributionWindowDays,
  ) => void;
  onAccountingModeChange: (accountingMode: INBAccountingMode) => void;
}

export const AttributionOptionsSelect = ({
  attributionModel,
  attributionWindow: selectedAttributionWindow,
  accountingMode,
  disabled,
  options,
  onAttributionModelChange,
  onAttributionWindowChange,
  onAccountingModeChange,
}: Props) => {
  const { user } = useUser();

  const { reconciledAttributionWindow } = useAttributionWindowOptions({
    timeGranularity: "daily",
    selectedAttributionWindow,
  });

  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [flip(), shift(), offset(4)],
  });

  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context, { enabled: !disabled });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const isCash = accountingMode === "cash";
  const isAttributionWindowVisible = !isCash;

  return (
    <div>
      <button
        disabled={disabled}
        className={clsx("text-xxs w-full h-10 border rounded-md px-3", {
          "border-v2-notice bg-v2-highlight-notice hover:border-v2-alien/400":
            isCash && !disabled,
          "border-v2-information bg-v2-highlight-information hover:border-v2-cosmic/400":
            !isCash && !disabled,
          "pointer-event-none border-v2-tertiary bg-v2-gray/10": disabled,
        })}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div
          className={clsx("flex items-center", {
            "text-v2-gray/300": disabled,
          })}
        >
          <i
            className={clsx("fa-regular text-base font-light mr-2", {
              "fa-sack-dollar text-v2-alien/400": isCash,
              "fa-magnifying-glass-dollar": !isCash,
            })}
          ></i>
          <div className="flex-1 text-left">
            <div className="font-semibold leading-3">
              {NBAccountingMode.getLabel(accountingMode)}
            </div>
            <small className="text-small leading-3">
              <span>
                {user.attributionMethodEnum.getLabel(attributionModel)}
              </span>
              {isAttributionWindowVisible && (
                <span>
                  (
                  {MCAttributionWindowDays.getLabel(
                    reconciledAttributionWindow,
                  )}
                  )
                </span>
              )}
            </small>
          </div>
          {renderChevronAnimation(
            isOpen,
            false,
            null,
            disabled
              ? "text-v2-gray/300"
              : isCash
              ? "text-v2-alien/400"
              : "text-v2-cosmic/400",
          )}
        </div>
      </button>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={refs.setFloating}
              style={floatingStyles}
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              className={clsx(
                "z-[2002] bg-white rounded-lg elevation-1 text-xs p-3 w-64 overflow-auto",
              )}
              {...getFloatingProps({
                onMouseDown: (e) => {
                  e.preventDefault();
                },
              })}
            >
              <div className="space-y-3 text-xs">
                <AttributionModelSelect
                  className="text-xs"
                  labelClassname="text-xs"
                  options={options}
                  attributionModel={attributionModel}
                  onChange={(value) => {
                    onAttributionModelChange(value);
                  }}
                />
                {isAttributionWindowVisible && (
                  <AttributionWindowSelect
                    className="whitespace-nowrap text-xs"
                    attributionModel={attributionModel}
                    attributionWindow={reconciledAttributionWindow ?? null}
                    timeGranularity="daily"
                    onChange={(value) => {
                      onAttributionWindowChange(value);
                    }}
                  />
                )}
                <NbAccountingModeSelect
                  className="text-xs"
                  nbAccountingMode={accountingMode}
                  isLoading={false}
                  icon={null}
                  onChange={(value) => {
                    onAccountingModeChange(value);
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};
